import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import { useMutation, useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { reservationApi, whatsappTemplateApi } from '../../services/api';
import { WhatsAppPreview, WhatsAppTemplate, WhatsAppTemplateType } from '../../types/whatsappTemplates';
import { getWhatsAppLink } from '../../utils/formatters';

interface WhatsAppSendDialogProps {
  open: boolean;
  onClose: () => void;
  documentType: 'reservation';
  documentId: string;
  documentLabel?: string;
}

const TEMPLATE_TYPE_BY_DOCUMENT: Record<WhatsAppSendDialogProps['documentType'], WhatsAppTemplateType> = {
  reservation: 'ORDER_CONFIRMATION',
};

export const WhatsAppSendDialog: React.FC<WhatsAppSendDialogProps> = ({ open, onClose, documentType, documentId, documentLabel }) => {
  const { t } = useTranslation();
  const templateType = TEMPLATE_TYPE_BY_DOCUMENT[documentType];

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [body, setBody] = useState('');

  const templatesQuery = useQuery<WhatsAppTemplate[]>(
    ['whatsappTemplates', templateType],
    () => whatsappTemplateApi.getAll(templateType),
    { enabled: open }
  );

  const activeTemplates = useMemo(
    () => (templatesQuery.data ?? []).filter((template) => template.active),
    [templatesQuery.data]
  );

  useEffect(() => {
    if (!open) {
      setSelectedTemplateId('');
      setRecipientPhone('');
      setBody('');
    }
  }, [open]);

  const previewMutation = useMutation(
    (templateId: number) =>
      documentType === 'reservation' ? reservationApi.previewWhatsApp(documentId, templateId) : Promise.reject(),
    {
      onSuccess: (preview: WhatsAppPreview) => {
        setRecipientPhone(preview.recipientPhone || '');
        setBody(preview.body);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('whatsappSend.previewFailed', 'Failed to load template preview'));
      },
    }
  );

  const handleTemplateChange = (templateId: number) => {
    setSelectedTemplateId(templateId);
    previewMutation.mutate(templateId);
  };

  const handleTemplateSelectChange = (value: number | '') => {
    if (value === '') {
      setSelectedTemplateId('');
      setRecipientPhone('');
      setBody('');
      return;
    }
    handleTemplateChange(value);
  };

  useEffect(() => {
    if (open && activeTemplates.length === 1 && selectedTemplateId === '') {
      handleTemplateChange(activeTemplates[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeTemplates, selectedTemplateId]);

  const handleSend = () => {
    if (!body.trim()) {
      toast.error(t('whatsappSend.bodyRequired', 'Enter a message'));
      return;
    }
    const link = getWhatsAppLink(recipientPhone);
    if (!link) {
      toast.error(t('whatsappSend.recipientRequired', 'Enter a valid recipient phone number'));
      return;
    }
    const url = `${link}?text=${encodeURIComponent(body)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.success(t('whatsappSend.opened', 'Opened in WhatsApp'));
    onClose();
  };

  const canSend = !previewMutation.isLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('whatsappSend.title', 'Send WhatsApp Message')}
        {documentLabel ? (
          <Typography variant="body2" color="text.secondary">
            {documentLabel}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {activeTemplates.length === 0 && !templatesQuery.isLoading ? (
            <Alert severity="info">
              {t(
                'whatsappSend.noTemplates',
                'No WhatsApp templates yet for this document type. Write your own message below, or create a template in Settings > WhatsApp Templates.'
              )}
            </Alert>
          ) : (
            <TextField
              select
              fullWidth
              label={t('whatsappSend.template', 'Template')}
              value={selectedTemplateId}
              onChange={(e) => handleTemplateSelectChange(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <MenuItem value="">{t('whatsappSend.noTemplateOption', "Don't use a template — write my own")}</MenuItem>
              {activeTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {previewMutation.isLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                label={t('whatsappSend.to', 'Phone number')}
                value={recipientPhone}
                onChange={(e) => setRecipientPhone(e.target.value)}
              />
              <TextField
                fullWidth
                multiline
                minRows={8}
                label={t('whatsappSend.body', 'Message')}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
        <Button
          variant="contained"
          startIcon={<Send />}
          disabled={!canSend}
          onClick={handleSend}
          sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1EBE57' } }}
        >
          {t('whatsappSend.send', 'Send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
